import { ThemeComponent } from '../../../../theming';
import {
  Component,
  computed,
  DoCheck,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { fromEvent } from 'rxjs';
import {
  FormInputSelectComponent,
  IObjectOption,
  SelectOption,
} from '../form-input-select.component';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import { ToSelectOptionAttributePipe } from '../../../../pipes/to-select-option-attribute/to-select-option-attribute.pipe';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { PopupPosition } from '../../../../directives/popup-position';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { EEntityType, ERelationType } from '@web/dtos';
import { CheckboxComponent } from '../checkbox/checkbox.component';

@Component({
  selector: 'aw-multi-select-drop-down',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    ToSelectOptionAttributePipe,
    AppendTestIdPipe,
    PopupPosition,
    FilterTextsPipe,
    LanguagePipe,
    CheckboxComponent
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  templateUrl: './multi-select-drop-down.component.html',
})
export class MultiSelectDropDownComponent
  extends FormInputSelectComponent
  implements OnInit, DoCheck
{
  protected readonly theme = inject(ThemeComponent);

  public readonly searchable: InputSignal<boolean> = input(false);
  public readonly searchPlaceholder: InputSignal<string> = input('Suchen');
  public readonly appearance: InputSignal<'default' | 'compact'> = input<
    'default' | 'compact'
  >('default');

  public readonly showList: WritableSignal<boolean> = signal(false);
  public readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);

  private readonly searchTerm: WritableSignal<string> = signal('');
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly attributePipe: ToSelectOptionAttributePipe =
    new ToSelectOptionAttributePipe();

  public readonly filteredOptions: Signal<SelectOption[]> = computed(() => {
    const searchTerm: string = this.searchTerm().trim().toLowerCase();
    if (!this.searchable() || !searchTerm) {
      return this.options();
    }

    return this.options().filter((option: SelectOption) =>
      this.optionLabel(option).toLowerCase().includes(searchTerm)
    );
  });

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  protected readonly optionControls: FormControl<boolean>[] = [];

  constructor(public override ngControl: NgControl) {
    super(ngControl);

    this.subscription.add(
      fromEvent<MouseEvent>(document, 'click').subscribe(
        (event: MouseEvent) => {
          const target: EventTarget | null = event.target;
          if (
            this.showList() &&
            target instanceof Node &&
            !this.elementRef.nativeElement.contains(target)
          ) {
            this.closeList();
          }
        }
      )
    );
  }

  ngDoCheck(): void {
    // Reuse controls by index, just like the option views in the template.
    while (this.optionControls.length < this.options().length) {
      this.optionControls.push(
        new FormControl(false, {
          nonNullable: true,
          validators: () => this.ngControl.control!.errors,
        })
      );
    }
    this.optionControls.length = this.options().length;
    const disabled = this.ngControl.control!.disabled;
    const errors = this.ngControl.control!.errors;
    const touched = this.ngControl.control!.touched;

    this.optionControls.forEach((control, index) => {
      const selected = this.selectedValues().includes(
        this.attributePipe.transform(
          this.options()[index],
          this.valueAttribute()
        )
      );
      // Keep the checkbox controls in sync without emitting boolean form values.
      if (control.value !== selected) {
        control.setValue(selected, { emitEvent: false });
      }
      if (control.disabled !== disabled) {
        if (disabled) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
      if (control.errors !== errors) {
        control.setErrors(errors, { emitEvent: false });
      }
      if (control.touched !== touched) {
        if (touched) {
          control.markAsTouched({ emitEvent: false });
        } else {
          control.markAsUntouched({ emitEvent: false });
        }
      }
    });
  }

  override ngOnInit(): void {
    this.subscription.add(
      this.searchControl.valueChanges.subscribe((value: string) => {
        this.searchTerm.set(value);
        this.selectedItem.set(undefined);
      })
    );
  }

  public toggleList(): void {
    if (this.ngControl.control!.disabled || this.isLoading()) {
      return;
    }

    if (!this.showList() && this.searchable()) {
      this.searchControl.setValue('');
    }
    this.showList.update((showList: boolean) => !showList);
    this.ngControl.control!.markAsTouched();
  }

  public closeList(): void {
    this.showList.set(false);
    this.selectedItem.set(undefined);
  }

  public handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeList();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      if (
        event.key === 'Enter' &&
        this.showList() &&
        this.selectedItem() !== undefined
      ) {
        event.preventDefault();
        this.selectOption(this.filteredOptions()[this.selectedItem()!]);
      }
      return;
    }

    event.preventDefault();
    if (!this.showList()) {
      this.showList.set(true);
    }

    const optionCount: number = this.filteredOptions().length;
    if (!optionCount) {
      this.selectedItem.set(undefined);
      return;
    }

    const currentIndex: number | undefined = this.selectedItem();
    const nextIndex: number =
      event.key === 'ArrowDown'
        ? currentIndex === undefined
          ? 0
          : Math.min(currentIndex + 1, optionCount - 1)
        : currentIndex === undefined
          ? optionCount - 1
          : Math.max(currentIndex - 1, 0);
    this.selectedItem.set(nextIndex);
  }

  public isOptionSelected(option: SelectOption): boolean {
    const optionValue: unknown = this.attributePipe.transform(
      option,
      this.valueAttribute()
    );
    return this.selectedValues().some((value: unknown) =>
      this.valuesAreEqual(value, optionValue)
    );
  }

  public areAllOptionsSelected(): boolean {
    return (
      this.options().length > 0 &&
      this.options().every((option: SelectOption) =>
        this.isOptionSelected(option)
      )
    );
  }

  public areSomeOptionsSelected(): boolean {
    return (
      !this.areAllOptionsSelected() &&
      this.options().some((option: SelectOption) =>
        this.isOptionSelected(option)
      )
    );
  }

  public toggleAllOptions(): void {
    if (this.ngControl.control!.disabled) {
      return;
    }

    const updatedValues: unknown[] = this.areAllOptionsSelected()
      ? []
      : this.options().map((option: SelectOption) =>
          this.attributePipe.transform(option, this.valueAttribute())
        );

    this.ngControl.control!.setValue(updatedValues);
    this.ngControl.control!.markAsTouched();
    this.ngControl.control!.markAsDirty();
    this.ngControl.control!.updateValueAndValidity();
  }

  public override selectOption(option: SelectOption): void {
    if (this.ngControl.control!.disabled) {
      return;
    }

    const selectedValue: unknown = this.attributePipe.transform(
      option,
      this.valueAttribute()
    );
    const selectedValues: unknown[] = this.selectedValues();
    const isSelected: boolean = selectedValues.some((value: unknown) =>
      this.valuesAreEqual(value, selectedValue)
    );
    const updatedValues: unknown[] = isSelected
      ? selectedValues.filter(
          (value: unknown) => !this.valuesAreEqual(value, selectedValue)
        )
      : [...selectedValues, selectedValue];

    this.ngControl.control!.setValue(updatedValues);
    this.ngControl.control!.markAsTouched();
    this.ngControl.control!.markAsDirty();
    this.ngControl.control!.updateValueAndValidity();
  }

  public selectedOptionLabels(): string {
    return this.selectedOptions()
      .map((option: SelectOption) => this.optionLabel(option))
      .join(', ');
  }

  public selectedOptions(): SelectOption[] {
    return this.options().filter((option: SelectOption) =>
      this.isOptionSelected(option)
    );
  }

  public removeSelectedOption(option: SelectOption, event: MouseEvent): void {
    event.stopPropagation();
    this.selectOption(option);
  }

  private selectedValues(): unknown[] {
    const value: unknown = this.ngControl.control!.value;
    return Array.isArray(value) ? value : [];
  }

  protected optionLabel(option: SelectOption): string {
    const label: unknown =
      this.name() && option && typeof option === 'object'
        ? (option as IObjectOption)[this.name()!]
        : option;
    return label?.toString() ?? '';
  }

  private valuesAreEqual(first: unknown, second: unknown): boolean {
    return first === second || JSON.stringify(first) === JSON.stringify(second);
  }
}
